import React, { useState, memo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Copy, Check } from "lucide-react";

/**
 * Renderizador de markdown das respostas da IA. Memoizado por conteúdo.
 */
function MarkdownMessageImpl({ content = "" }) {
    return (
        <div className="markdown-body">
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    code: CodeRenderer,
                    a: ({ node, ...props }) => (
                        <a {...props} target="_blank" rel="noopener noreferrer" />
                    ),
                    table: ({ node, ...props }) => (
                        <div className="overflow-x-auto my-3">
                            <table className="border-collapse border border-border text-sm" {...props} />
                        </div>
                    ),
                    th: ({ node, ...props }) => (
                        <th className="border border-border bg-muted px-3 py-1.5 text-left text-xs font-semibold" {...props} />
                    ),
                    td: ({ node, ...props }) => (
                        <td className="border border-border px-3 py-1.5" {...props} />
                    ),
                }}
            >
                {content}
            </ReactMarkdown>
        </div>
    );
}

function CodeRenderer({ inline, className, children, ...props }) {
    const codeString = String(children).replace(/\n$/, "");
    const match = /language-(\w+)/.exec(className || "");
    const lang = match?.[1];

    const looksInline = inline || (!lang && !codeString.includes("\n"));

    if (looksInline) {
        return (
            <code
                className="bg-muted border border-border rounded-sm px-1.5 py-0.5 text-[0.85em] font-mono text-foreground"
                {...props}
            >
                {children}
            </code>
        );
    }

    return <CodeBlock code={codeString} lang={lang} />;
}

function CodeBlock({ code, lang }) {
    const [copied, setCopied] = useState(false);
    const displayLang = lang || "texto";

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            /* noop */
        }
    };

    return (
        <div
            className="my-3 rounded-sm border border-border overflow-hidden bg-[#1e1e1e]"
            data-testid="code-block"
        >
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-border bg-[#252526]">
                <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground">
                    {displayLang}
                </span>
                <button
                    type="button"
                    onClick={handleCopy}
                    className="flex items-center gap-1.5 text-[11px] font-mono text-muted-foreground hover:text-primary transition-colors px-2 py-1 rounded-sm hover:bg-muted/60"
                    data-testid="code-copy-btn"
                >
                    {copied ? (
                        <>
                            <Check className="w-3 h-3" /> Copiado
                        </>
                    ) : (
                        <>
                            <Copy className="w-3 h-3" /> Copiar
                        </>
                    )}
                </button>
            </div>
            <SyntaxHighlighter
                language={lang || "text"}
                style={vscDarkPlus}
                PreTag="div"
                customStyle={{
                    margin: 0,
                    padding: "12px",
                    fontSize: "12px",
                    lineHeight: "1.6",
                    background: "transparent",
                }}
                codeTagProps={{ style: { fontFamily: "'IBM Plex Mono', Menlo, monospace" } }}
            >
                {code}
            </SyntaxHighlighter>
        </div>
    );
}

const MarkdownMessage = memo(MarkdownMessageImpl, (a, b) => a.content === b.content);
export default MarkdownMessage;
